<template>
  <div class="user-menu-dropdown">
    <ul class="user-menu-dropdown__links-list">
      <LinkWithIcon
        v-if="props.orgInContext?.customerId"
        class="user-menu-dropdown__link"
        tag="RouterLink"
        :to="{ name: 'CommunityPageAdmin', params: { pageId: props.orgInContext?.customerId } }"
        @click="emit('requestClose')"
      >
        <template #icon><IconEmbedded name="gear_2" :size="22" style="left: 0px" /></template>
        <span style="font-weight: 400">Settings</span>
      </LinkWithIcon>

      <LinkWithIcon
        v-if="props.orgInContext?.customerId && props.orgInContext?.subType !== 'CustomCollab'"
        class="user-menu-dropdown__link"
        tag="RouterLink"
        :to="{ name: 'SubscriptionSettings', params: { pageId: props.orgInContext.customerId } }"
        @click="emit('requestClose')"
      >
        <template #icon><IconEmbedded name="dollar_2" :size="22" style="left: 0px" /></template>
        <span style="font-weight: 400">Subscription / Billing</span>
      </LinkWithIcon>

      <LinkWithIcon
        v-if="props.orgInContext?.customerId"
        class="user-menu-dropdown__link"
        tag="RouterLink"
        :to="{ name: 'UserAdmin', params: { pageId: props.orgInContext.customerId } }"
        @click="emit('requestClose')"
      >
        <template #icon><IconEmbedded name="users_2" :size="22" style="left: 0px" /></template>
        <span style="font-weight: 400">Users</span>
      </LinkWithIcon>
    </ul>

    <div class="user-menu-dropdown__divider"></div>

    <ul class="user-menu-dropdown__links-list">
      <LinkWithIcon v-if="globalAuth?.isAuthenticated && profileLink" class="user-menu-dropdown__link" tag="RouterLink" @click="emit('requestClose')" :to="profileLink">
        <template #icon><IconEmbedded name="user_2" :size="22" style="left: 0px" /></template>
        <span style="font-weight: 400">{{ userProfileStore.getViewingUserProfile?.firstName }}</span>
      </LinkWithIcon>
      <LinkWithIcon v-else-if="globalAuth?.isAuthenticated" class="user-menu-dropdown__link" tag="div" :isDisabled="true">
        <template #icon><IconEmbedded name="user_2" :size="22" style="left: 0px" /></template>
        <span style="font-weight: 400">{{ userProfileStore.getViewingUserProfile?.firstName }}</span>
      </LinkWithIcon>

      <LinkWithIcon
        class="user-menu-dropdown__link user-menu-dropdown__link--red"
        tag="li"
        hoverColor="red"
        @click="
          () => {
            emit('requestClose');
            logout();
          }
        "
      >
        <template #icon><IconEmbedded name="exit_2" :size="22" /></template>
        <span style="font-weight: 400">Sign out</span>
      </LinkWithIcon>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { inject, computed } from "vue";
import { useRouter, RouteLocationRaw } from "vue-router";
import { useUserProfileStore } from "@stores/userProfileStore";

// Types
import { PartnerNavInfo } from "@contracts/partnerNavInfo";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";

// Stores
const userProfileStore = useUserProfileStore();

// Global variables & injections
const globalAuth = inject("globalAuth") as any;

const router = useRouter();

const props = withDefaults(
  defineProps<{
    orgInContext: PartnerNavInfo | null;
  }>(),
  {
    orgInContext: null,
  }
);

const emit = defineEmits<{
  (e: "requestClose"): void;
}>();

// Handles the logout =========================================================
function logout() {
  globalAuth.logout();
  router.push({ name: "Home" });
}

// Generates profile link =====================================================
const profileLink = computed<RouteLocationRaw | null>(() => {
  const creatorId = userProfileStore.getViewingUserProfile?.sherpaId;
  if (!creatorId) return null;
  if (userProfileStore.getViewingUserProfile?.adminOf?.length > 0) {
    return { name: "CustomerAdminProfile", params: { creatorId } };
  }
  return { name: "CreatorProfilePublic", params: { creatorId } };
});
</script>

<style scoped lang="scss">
// User menu dropdown =========================================================
.user-menu-dropdown {
  width: 88dvw;
  max-width: 310px;
  padding: 20px 20px 24px;
  box-sizing: border-box;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 0;
  background: rgba(243, 243, 243, 1);
  box-shadow: 0 15px 20px -5px rgba(0, 0, 0, 0.13);

  &::before {
    content: "";
    width: 15px;
    height: 15px;
    position: absolute;
    inset: -6px auto auto 43px;
    transform: rotate(45deg);
    background: rgba(243, 243, 243, 1);
    pointer-events: none;
  }

  &__links-list {
    gap: 12px;
    padding: 0;
    margin: 0 0 20px;
    display: flex;
    flex-direction: column;
    list-style: none;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__link {
    :deep(span) {
      margin-left: 4px;
      font-size: 15px;
      text-decoration: none;
    }

    &:hover :deep(span) {
      text-decoration: underline;
      text-decoration-color: rgba(0, 0, 0, 0.3);
      text-underline-offset: 3px;
    }
  }

  &__divider {
    margin-bottom: 15px;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  }
}
</style>
